import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Clock, Dices, Flame, Search, SlidersHorizontal, X } from "lucide-react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { CosmicShell } from "@/components/whatif/cosmic-shell";
import { useWhatIfScenarios } from "@/hooks/use-whatif-scenarios";
import { WHATIF_CATEGORIES, CATEGORY_BY_ID } from "@/lib/whatif/categories";
import {
  CERTAINTY_FILTERS,
  matchScenario,
  pickRandom,
  scenarioCertainties,
  sortScenarios,
  type WhatIfSort,
} from "@/lib/whatif/dataset";
import { readAllProgress } from "@/lib/whatif/progress";
import { readRecentScenarios, clearRecentScenarios } from "@/lib/whatif/recent";
import { CertaintyBadge } from "@/components/whatif/certainty-badge";
import type { Certainty, WhatIfCategoryId, WhatIfScenario } from "@/lib/whatif/types";

export const Route = createFileRoute("/what-if/")({
  head: () => ({
    ...seo({
      title: "WHAT IF? LAB — Mind-Bending Science Scenarios",
      description:
        "Change one rule and watch reality react. Explore interactive, stage-by-stage science scenarios grounded in real physics, astronomy and biology.",
      path: "/what-if",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "What If? Lab", path: "/what-if" },
      ]),
    ],
  }),
  component: WhatIfLanding,
});

const SORTS: { id: WhatIfSort; label: string }[] = [
  { id: "featured", label: "Featured" },
  { id: "az", label: "A–Z" },
  { id: "category", label: "Category" },
];

function ScenarioRow({ s, state }: { s: WhatIfScenario; state?: "done" | "started" }) {
  return (
    <Link
      to="/what-if/$slug"
      params={{ slug: s.slug }}
      className="whatif-surface flex items-start gap-3 rounded-[18px] p-3.5 transition-transform active:scale-[0.99]"
    >
      <span className="text-lg leading-none" aria-hidden>
        {CATEGORY_BY_ID.get(s.category)?.emoji}
      </span>
      <span className="min-w-0 flex-1">
        <span className="whatif-display block text-sm font-bold leading-snug">{s.title}</span>
        <span className="mt-0.5 block text-xs text-[color:var(--wi-ink-dim)]">
          {s.shortDescription}
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full border border-[color:var(--wi-line)] px-2 py-0.5 text-[10px] font-medium text-[color:var(--wi-ink-dim)]">
            {CATEGORY_BY_ID.get(s.category)?.name}
          </span>
          {s.trending ? (
            <span className="rounded-full bg-orange-400/15 px-2 py-0.5 text-[10px] font-semibold text-orange-300">
              Trending
            </span>
          ) : null}
          <CertaintyBadge value={scenarioCertainties(s)[0]} />
          {state === "done" ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300/25 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-200">
              <Check className="size-2.5" /> Explored
            </span>
          ) : state === "started" ? (
            <span className="rounded-full border border-[color:var(--wi-line)] bg-white/[0.04] px-2 py-0.5 text-[10px] font-semibold text-[color:var(--wi-ink-dim)]">
              In progress
            </span>
          ) : null}
        </span>
      </span>
    </Link>
  );
}

function WhatIfLanding() {
  const navigate = useNavigate();
  const { scenarios } = useWhatIfScenarios();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<WhatIfCategoryId | null>(null);
  const [certainty, setCertainty] = useState<Certainty | null>(null);
  const [sort, setSort] = useState<WhatIfSort>("featured");
  const [showFilters, setShowFilters] = useState(false);
  const [recentSlugs, setRecentSlugs] = useState<string[]>([]);
  const [progress, setProgress] = useState<Record<string, { stage: number; done: boolean }>>({});

  // Browsing is free for everyone; the premium check happens on the scenario page.
  useEffect(() => {
    setRecentSlugs(readRecentScenarios().map((r) => r.slug));
    setProgress(readAllProgress());
  }, []);


  const all = useMemo(() => scenarios ?? [], [scenarios]);
  const results = useMemo(
    () =>
      sortScenarios(
        all.filter(
          (s) =>
            matchScenario(s, query) &&
            (!category || s.category === category) &&
            (!certainty || scenarioCertainties(s).includes(certainty)),
        ),
        sort,
      ),
    [all, query, category, certainty, sort],
  );
  const trending = useMemo(() => all.filter((s) => s.trending), [all]);
  const countByCategory = useMemo(() => {
    const m = new Map<WhatIfCategoryId, number>();
    for (const s of all) m.set(s.category, (m.get(s.category) ?? 0) + 1);
    return m;
  }, [all]);
  const recent = useMemo(
    () =>
      recentSlugs
        .map((slug) => all.find((s) => s.slug === slug))
        .filter((s): s is WhatIfScenario => Boolean(s))
        .slice(0, 6),
    [recentSlugs, all],
  );

  const surprise = () => {
    const pick = pickRandom(all);
    if (pick) void navigate({ to: "/what-if/$slug", params: { slug: pick.slug } });
  };

  const filtering = query.trim().length > 0 || category !== null || certainty !== null;
  const resetFilters = () => {
    setQuery("");
    setCategory(null);
    setCertainty(null);
  };

  if (!scenarios) {
    return (
      <CosmicShell>
        <div className="pt-6">
          <div className="whatif-surface h-44 animate-pulse rounded-[24px]" />
          <div className="whatif-surface mt-6 h-56 animate-pulse rounded-[24px]" />
        </div>
      </CosmicShell>
    );
  }


  return (
    <CosmicShell>
      <header className="pt-4">
        <h1 className="whatif-display text-4xl font-bold leading-[0.95]">
          WHAT IF?
          <span className="block bg-[linear-gradient(90deg,#4fd6e8,#7c4dff)] bg-clip-text text-transparent">
            LAB
          </span>
        </h1>
        <p className="mt-2 text-sm italic text-[color:var(--wi-ink-dim)]">
          Change one rule. Watch reality react.
        </p>
        <p className="mt-1 text-xs text-[color:var(--wi-ink-dim)]">
          {all.length} scenarios · {WHATIF_CATEGORIES.length} categories
        </p>
      </header>

      <div className="mt-5 flex gap-2">
        <label className="whatif-surface flex h-12 min-w-0 flex-1 items-center gap-2 rounded-2xl px-4">
          <Search className="size-4 shrink-0 text-[color:var(--wi-ink-dim)]" />
          <span className="sr-only">Search scenarios by title, description or tag</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search titles, topics, tags…"
            enterKeyHint="search"
            className="w-full min-w-0 bg-transparent text-sm text-[color:var(--wi-ink)] outline-none placeholder:text-[color:var(--wi-ink-dim)]"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="-mr-1 grid size-7 shrink-0 place-items-center rounded-full bg-white/10"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </label>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="whatif-filters"
          className={`whatif-surface grid size-12 shrink-0 place-items-center rounded-2xl ${
            certainty || sort !== "featured" ? "border-[color:var(--wi-cyan)]" : ""
          }`}
        >
          <span className="sr-only">Filters and sorting</span>
          <SlidersHorizontal className="size-4" />
        </button>
      </div>

      {showFilters ? (
        <section id="whatif-filters" className="whatif-surface mt-3 rounded-[20px] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--wi-ink-dim)]">
            Sort by
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {SORTS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSort(s.id)}
                aria-pressed={sort === s.id}
                className={`min-h-9 rounded-full border px-3 text-xs font-semibold transition-colors ${
                  sort === s.id
                    ? "border-[color:var(--wi-cyan)] text-white"
                    : "border-[color:var(--wi-line)] text-[color:var(--wi-ink-dim)]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--wi-ink-dim)]">
            Certainty
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {CERTAINTY_FILTERS.map((c) => {
              const active = certainty === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCertainty(active ? null : c.id)}
                  aria-pressed={active}
                  className={`min-h-9 rounded-full border px-3 text-xs font-semibold transition-colors ${
                    active
                      ? "border-[color:var(--wi-cyan)] text-white"
                      : "border-[color:var(--wi-line)] text-[color:var(--wi-ink-dim)]"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {filtering ? (
        <button
          type="button"
          onClick={resetFilters}
          className="mt-3 inline-flex min-h-9 items-center gap-1.5 rounded-full border border-[color:var(--wi-line)] px-3 text-xs font-semibold text-[color:var(--wi-ink-dim)]"
        >
          <X className="size-3.5" /> Clear filters
        </button>
      ) : null}

      {!filtering && recent.length > 0 ? (
        <section className="mt-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="whatif-display flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em]">
              <Clock className="size-4 text-[color:var(--wi-cyan)]" /> Recently explored
            </h2>
            <button
              type="button"
              onClick={() => {
                clearRecentScenarios();
                setRecentSlugs([]);
              }}
              className="text-[11px] font-semibold text-[color:var(--wi-ink-dim)]"
            >
              Clear
            </button>
          </div>
          <div className="-mx-4 mt-3 flex snap-x gap-2 overflow-x-auto px-4 pb-2">
            {recent.map((s) => (
              <Link
                key={s.id}
                to="/what-if/$slug"
                params={{ slug: s.slug }}
                className="whatif-surface flex max-w-[240px] shrink-0 snap-start items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold active:scale-[0.98]"
              >
                <span aria-hidden>{CATEGORY_BY_ID.get(s.category)?.emoji}</span>
                <span className="truncate">{s.title}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {!filtering ? (
        <section className="mt-6">
          <h2 className="whatif-display flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em]">
            <Flame className="size-4 text-orange-300" /> Trending What Ifs
          </h2>
          <div className="-mx-4 mt-3 flex snap-x gap-3 overflow-x-auto px-4 pb-2">
            {trending.map((s) => (
              <Link
                key={s.id}
                to="/what-if/$slug"
                params={{ slug: s.slug }}
                className="whatif-surface relative w-[70%] min-w-[210px] max-w-[260px] shrink-0 snap-start overflow-hidden rounded-[20px] p-4 transition-transform active:scale-[0.98]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-[linear-gradient(140deg,rgb(79_214_232/0.14),transparent_55%)]"
                />
                <span className="relative flex items-center justify-between">
                  <span className="text-lg">{CATEGORY_BY_ID.get(s.category)?.emoji}</span>
                  <span className="rounded-full bg-orange-400/15 px-2 py-0.5 text-[10px] font-semibold text-orange-300">
                    Trending
                  </span>
                </span>
                <p className="whatif-display relative mt-1.5 text-sm font-bold leading-snug">
                  {s.title}
                </p>
                <p className="relative mt-1 line-clamp-2 text-xs text-[color:var(--wi-ink-dim)]">
                  {s.shortDescription}
                </p>
                <p className="relative mt-2 text-[10px] font-medium uppercase tracking-[0.12em] text-[color:var(--wi-ink-dim)]">
                  {s.stages.length} stages
                </p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-6">
        <h2 className="whatif-display text-sm font-bold uppercase tracking-[0.12em]">Categories</h2>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setCategory(null)}
            aria-pressed={category === null}
            className={`whatif-surface col-span-2 flex min-h-11 items-center justify-between gap-2 rounded-2xl px-3 py-2.5 text-xs font-semibold transition-colors ${
              category === null
                ? "border-[color:var(--wi-cyan)] text-white"
                : "text-[color:var(--wi-ink-dim)]"
            }`}
          >
            <span>🔭 All categories</span>
            <span className="text-[10px]">{all.length}</span>
          </button>
          {WHATIF_CATEGORIES.map((c) => {
            const active = category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(active ? null : c.id)}
                aria-pressed={active}
                className={`whatif-surface flex min-h-11 items-center gap-2 rounded-2xl px-3 py-2.5 text-left text-xs font-medium transition-colors ${
                  active ? "border-[color:var(--wi-cyan)] text-white" : "text-[color:var(--wi-ink-dim)]"
                }`}
              >
                <span aria-hidden>{c.emoji}</span>
                <span className="min-w-0 flex-1 leading-tight">{c.name}</span>
                <span className="shrink-0 rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] tabular-nums">
                  {countByCategory.get(c.id) ?? 0}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <button
        type="button"
        onClick={surprise}
        className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(90deg,#3b6dff,#7c4dff)] text-sm font-bold uppercase tracking-[0.12em] text-white transition-transform active:scale-[0.98]"
      >
        <Dices className="size-5" /> Surprise me
      </button>

      <section className="mt-7">
        <h2 className="whatif-display text-sm font-bold uppercase tracking-[0.12em]">
          {filtering
            ? `${results.length} scenario${results.length === 1 ? "" : "s"}`
            : "All scenarios"}
        </h2>
        <p aria-live="polite" className="sr-only">
          {scenarios ? `${results.length} scenarios listed` : "Loading scenarios"}
        </p>
        <ul className="mt-3 space-y-2">
          {results.map((s) => (
            <li key={s.id}>
              <ScenarioRow
                s={s}
                state={
                  progress[s.slug]?.done ? "done" : progress[s.slug] ? "started" : undefined
                }
              />
            </li>
          ))}
          {scenarios && results.length === 0 ? (
            <li className="whatif-surface rounded-[18px] p-5 text-center text-sm text-[color:var(--wi-ink-dim)]">
              Nothing matches that yet — try another word or{" "}
              <button
                type="button"
                onClick={resetFilters}
                className="font-semibold text-[color:var(--wi-cyan)] underline"
              >
                clear the filters
              </button>
              .
            </li>
          ) : null}
          {!scenarios
            ? Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className="whatif-surface h-16 animate-pulse rounded-[18px]" />
              ))
            : null}
        </ul>
      </section>
    </CosmicShell>
  );
}
